/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { DeviceDetailsProps } from "../types";
import Ru2DeviceDetails from "./Ru2DeviceDetails";

/**
 * Normalizes MA6200-G2 RU3 remote attributes to match the format expected by Ru2DeviceDetails.
 *
 * Key differences between RU3 (G2) and RU2 (G1) attribute formats:
 *  - G2 optical port power values are in dBm directly; Ru2DeviceDetails divides by 100
 *  - RU3 has Master/Slave optical port alarms but no Master/Slave power readings (will show N/A)
 */
const normalizeRu3Attributes = (parsedAttributes: any): any => {
  if (!parsedAttributes) return {};

  // 1. Scale optical port power ×100 so Ru2DeviceDetails ÷100 gives the correct dBm value
  const rawLanConn = parsedAttributes.lanConnectivity || {};
  const lanConnectivity = { ...rawLanConn };
  for (let n = 1; n <= 8; n++) {
    for (const dir of ["RX", "TX"]) {
      const key = `op${n}Transceiver${dir}power`;
      if (key in lanConnectivity) {
        const val = parseFloat(lanConnectivity[key]);
        if (!isNaN(val) && val !== 0) {
          lanConnectivity[key] = String(val * 100);
        }
      }
    }
  }

  return {
    ...parsedAttributes,
    lanConnectivity

  };
};

const Ru3DeviceDetails: React.FC<DeviceDetailsProps> = (props) => {
  const normalizedParsedAttributes = React.useMemo(
    () => normalizeRu3Attributes(props.parsedAttributes),
    [props.parsedAttributes]
  );

  return <Ru2DeviceDetails {...props} parsedAttributes={normalizedParsedAttributes} />;
};

export default Ru3DeviceDetails;
